import {
  Box,
  Button,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  Select,
  Switch
} from '@mui/material';
import { UploadSimple } from '@phosphor-icons/react';
import { AxiosResponse } from 'axios';
import { memo, useEffect, useRef, useState } from 'react';
import { useEdges } from 'reactflow';
import '../../assets/styles/animatedBorder.scss';
import {
  SqlQuerierSchema,
  SqlQuerierService,
} from '../../service/SqlQuerierService';
import useFlowsStore from '../../store/storeFlows';
import { useNotificationStore } from '../../store/storeNotifications';
import { nodeDataAutoSaveDynamic } from '../../util/autosave/nodedata_autosave';
import { checkExecuteTrigger } from '../../util/checkExecute';
import { isActiveEdge } from '../../util/findActiveHandle';
import { NodeProps } from '../../util/Types/NodeProps';
import { useDebounceEffect } from '../../util/useDebounceEffect';
import TagInput from '../Test/TagInput';
import Label from '../UI/Label/Label';
import Node from '../UI/Node/Node';
import { nodeColorBasedOnType } from '../UI/Node/nodeConstants';
import OutputTextarea from '../UI/OutputTextarea/OutputTextarea';
import { VisuallyHiddenInput } from '../UI/VisuallyHidenInput';

export enum DatabaseOptions {
  MySQL = 'MySQL',
  PostgreSQL = 'PostgreSQL',
  MongoDB = 'MongoDB',
  SqlServer = 'SqlServer',
  Firebase = 'Firebase',
}

export const sourceTypes = {
  host: 'host',
  port: 'port',
  database: 'database',
  username: 'username',
  password: 'password',
  connectionString: 'connectionString',
  collection: 'collection',
  filter: 'filter',
  query: 'query',
};

type FormDataType = {
  host: string;
  port: string;
  database: string;
  username: string;
  password: string;
  dbms: DatabaseOptions;
  ssl: boolean;
  ca: string;
  query: string;
  connectionString: string;
  collection: string;
  filter: string;
  response: AxiosResponse | null | any;
  text: string;
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
};

function SqlQuerier({ id, data, isConnectable, type, selected }: NodeProps) {
  const edges: any = useEdges();
  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState<FormDataType>({
    host: data?.host || '',
    port: data?.port || '',
    database: data?.database || '',
    username: data?.username || '',
    password: data?.password || '',
    ssl: data?.ssl || false,
    ca: data?.ca || '',
    dbms: data?.dbms || DatabaseOptions.MySQL,
    query: data?.query || '',
    connectionString: data?.connectionString || '',
    collection: data?.collection || '',
    filter: data?.filter || '',
    response: data?.response || null,
    text: data?.text || '',
    apiKey: data?.apiKey || '',
    authDomain: data?.authDomain || '',
    projectId: data?.projectId || '',
    storageBucket: data?.storageBucket || '',
    messagingSenderId: data?.messagingSenderId || '',
    appId: data?.appId || '',
  });

  const setNotification = useNotificationStore(state => state.setNotification);

  const [hasError, setHasError] = useState(false);

  const [isChangingDirectly, setIsChangingDirectly] = useState(false);
  const [hasToExecute, setHasToExecute] = useState(true);

  const [whoIsChanging, setWhoIsChanging] = useState<{
    value: any;
    name: string;
  }>({
    value: '',
    name: '',
  });
  const { setNodeState, setSchema, setSaving, flowId } = useFlowsStore(
    state => state,
  );

  useDebounceEffect(
    () => {
      if (isChangingDirectly) {
        setIsChangingDirectly(false);
        nodeDataAutoSaveDynamic({
          newEdges: edges,
          setSaving,
          setNodeState,
          id,
          flowId,
          objectData: {
            [whoIsChanging.name]: whoIsChanging.value,
          },
          setSchema,
        });

        setWhoIsChanging({
          value: '',
          name: '',
        });
      }
    },
    [isChangingDirectly, whoIsChanging],
    300,
  );

  const onSave = (
    object: { [key: string]: any },
    changeType: string = 'default',
  ) => {
    nodeDataAutoSaveDynamic({
      newEdges: edges,
      setNodeState,
      id,
      setSaving,
      flowTriggerData: checkExecuteTrigger(data, id),
      changeType,
      flowId,
      objectData: {
        ...object,
      },
      setSchema,
    });
  };

  const handleSendRequest = async () => {
    setIsLoading(true);

    try {
      const dataWithConnected = {
        ...formData,
        hostData: data?.hostData || [],
        portData: data?.portData || [],
        databaseData: data?.databaseData || [],
        usernameData: data?.usernameData || [],
        passwordData: data?.passwordData || [],
        queryData: data?.queryData || [],
        connectionStringData: data?.connectionStringData || [],
        collectionData: data?.collectionData || [],
        filterData: data?.filterData || [],
        apiKeyData: data?.apiKeyData || [],
        authDomainData: data?.authDomainData || [],
        projectIdData: data?.projectIdData || [],
        storageBucketData: data?.storageBucketData || [],
        messagingSenderIdData: data?.messagingSenderIdData || [],
        appIdData: data?.appIdData || [],
      };

      const schemaResponse = await SqlQuerierSchema.safeParseAsync(dataWithConnected);

      if (schemaResponse.success === false) {
        console.log(schemaResponse.error);
        setNotification({
          type: 'error',
          message: schemaResponse.error.issues[0].message,
        });

        return;
      }

      const response = await SqlQuerierService.sendQuery(schemaResponse.data);

      onSave(
        {
          response: response,
          text: JSON.stringify(response.data, null, 2),
        },
        'execute',
      );

      setFormData({
        ...formData,
        response: response,
        text: JSON.stringify(response.data, null, 2),
      });
      setHasError(false);
    } catch (error: any) {
      setFormData({
        ...formData,
        response: error?.response,
        text: JSON.stringify(error?.response, null, 2),
      });
      onSave(
        {
          text: JSON.stringify(error?.response, null, 2),
        },
        'execute',
      );
      setHasError(true);

      setNotification({
        type: 'error',
        message: error.message,
      });
    } finally {
      setHasToExecute(true);
      setIsLoading(false);
    }
  };

  const debounceTimeoutRef = useRef<any>(null);

  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    if (data?.flowTrigger?.id && !data?.canceled && !data?.paused) {
      if (hasToExecute) {
        debounceTimeoutRef.current = setTimeout(() => {
          setHasToExecute(false);
          handleSendRequest();
        }, 1000); // Adjust the debounce delay as needed (e.g., 300 milliseconds)
      }
    }

    return () => {
      clearTimeout(debounceTimeoutRef.current);
    };
  }, [hasToExecute, formData, data]);

  const onChange = (e: any) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setIsChangingDirectly(true);

    setWhoIsChanging({ name: e.target.name, value: e.target.value });
  };

  const onChangeSwitch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.checked,
    });

    setIsChangingDirectly(true);
    setWhoIsChanging({ name: e.target.name, value: e.target.checked });
  };

  const nodeType =
    nodeColorBasedOnType[type as keyof typeof nodeColorBasedOnType];
  const findColor = nodeType.colors;

  const renderDatabaseInputs = () => {
    switch (formData.dbms) {
      case 'MySQL':
      case 'PostgreSQL':
      case 'SqlServer':
        return (
          <>
            <TagInput
              name="host"
              placeholder="https://example.com"
              labelName={'Host'}
              handleId={'host'}
              isCollapsed={true}
              nodeId={id}
              isActive={isActiveEdge(edges, id, 'host', 'target')}
              isAdvanced={true}
              findColor={findColor}
              removeIcons
              className="nodrag"
              advancedCollapsed={true}
              onChange={onChange}
              dataConnected={data?.hostData || []}
              placement={data?.placement}
              value={formData.host}
            />

            <TagInput
              name="port"
              placeholder={formData.dbms === 'MySQL' ? '3306' : '5432'}
              labelName={'Port'}
              handleId={'port'}
              isCollapsed={true}
              nodeId={id}
              isActive={isActiveEdge(edges, id, 'port', 'target')}
              isAdvanced={true}
              findColor={findColor}
              removeIcons
              className="nodrag"
              advancedCollapsed={true}
              onChange={onChange}
              dataConnected={data?.portData || []}
              placement={data?.placement}
              value={formData.port}
            />

            <TagInput
              name="database"
              placeholder="database-name"
              labelName={'Database Name'}
              handleId={'database'}
              isCollapsed={true}
              nodeId={id}
              isActive={isActiveEdge(edges, id, 'database', 'target')}
              isAdvanced={true}
              findColor={findColor}
              removeIcons
              className="nodrag"
              advancedCollapsed={true}
              onChange={onChange}
              dataConnected={data?.databaseData || []}
              placement={data?.placement}
              value={formData.database}
            />

            <TagInput
              name="username"
              placeholder="database-user"
              labelName={'Username'}
              handleId={'username'}
              isCollapsed={true}
              nodeId={id}
              isActive={isActiveEdge(edges, id, 'username', 'target')}
              isAdvanced={true}
              findColor={findColor}
              removeIcons
              className="nodrag"
              advancedCollapsed={true}
              onChange={onChange}
              dataConnected={data?.usernameData || []}
              placement={data?.placement}
              value={formData.username}
            />

            <TagInput
              name="password"
              placeholder="password"
              labelName={'Password'}
              handleId={'password'}
              isCollapsed={true}
              nodeId={id}
              isActive={isActiveEdge(edges, id, 'password', 'target')}
              isAdvanced={true}
              findColor={findColor}
              removeIcons
              className="nodrag"
              advancedCollapsed={true}
              onChange={onChange}
              dataConnected={data?.passwordData || []}
              placement={data?.placement}
              value={formData.password}
            />

            <TagInput
              name="query"
              placeholder="SELECT * FROM users"
              labelName={'Query'}
              handleId={'query'}
              isCollapsed={true}
              nodeId={id}
              isActive={isActiveEdge(edges, id, 'query', 'target')}
              isAdvanced={true}
              findColor={findColor}
              removeIcons
              height={'100px'}
              className="nodrag"
              advancedCollapsed={true}
              onChange={onChange}
              dataConnected={data?.queryData || []}
              placement={data?.placement}
              value={formData.query}
            />
          </>
        );
      case 'MongoDB':
        return (
          <>
            <TagInput
              name="connectionString"
              placeholder="Put your connection string here"
              labelName={'Connection String'}
              handleId={'connectionString'}
              isCollapsed={true}
              nodeId={id}
              isActive={isActiveEdge(edges, id, 'connectionString', 'target')}
              isAdvanced={true}
              findColor={findColor}
              removeIcons
              className="nodrag"
              advancedCollapsed={true}
              onChange={onChange}
              dataConnected={data?.connectionStringData || []}
              placement={data?.placement}
              value={formData.connectionString}
            />

            <TagInput
              name="database"
              placeholder="database-name"
              labelName={'Database Name'}
              handleId={'database'}
              isCollapsed={true}
              nodeId={id}
              isActive={isActiveEdge(edges, id, 'database', 'target')}
              isAdvanced={true}
              findColor={findColor}
              removeIcons
              className="nodrag"
              advancedCollapsed={true}
              onChange={onChange}
              dataConnected={data?.databaseData || []}
              placement={data?.placement}
              value={formData.database}
            />

            <TagInput
              name="collection"
              placeholder="your_collection"
              labelName={'Collection'}
              handleId={'collection'}
              isCollapsed={true}
              nodeId={id}
              isActive={isActiveEdge(edges, id, 'collection', 'target')}
              isAdvanced={true}
              findColor={findColor}
              removeIcons
              className="nodrag"
              advancedCollapsed={true}
              onChange={onChange}
              dataConnected={data?.collectionData || []}
              placement={data?.placement}
              value={formData.collection}
            />

            <TagInput
              name="filter"
              placeholder='{"field": "value"}'
              labelName={'Filter'}
              handleId={'filter'}
              isCollapsed={true}
              nodeId={id}
              isActive={isActiveEdge(edges, id, 'filter', 'target')}
              isAdvanced={true}
              findColor={findColor}
              removeIcons
              height={'100px'}
              className="nodrag"
              advancedCollapsed={true}
              onChange={onChange}
              dataConnected={data?.filterData || []}
              placement={data?.placement}
              value={formData.filter}
            />
          </>
        );
      case 'Firebase':
        return (
          <>
            <TagInput
              name="apiKey"
              placeholder="Firebase API Key"
              labelName={'API Key'}
              handleId={'apiKey'}
              isCollapsed={true}
              nodeId={id}
              isActive={isActiveEdge(edges, id, 'apiKey', 'target')}
              isAdvanced={true}
              findColor={findColor}
              removeIcons
              className="nodrag"
              advancedCollapsed={true}
              onChange={onChange}
              dataConnected={data?.apiKeyData || []}
              placement={data?.placement}
              value={formData.apiKey}
            />

            <TagInput
              name="authDomain"
              placeholder="example.firebaseapp.com"
              labelName={'Auth Domain'}
              handleId={'authDomain'}
              isCollapsed={true}
              nodeId={id}
              isActive={isActiveEdge(edges, id, 'authDomain', 'target')}
              isAdvanced={true}
              findColor={findColor}
              removeIcons
              className="nodrag"
              advancedCollapsed={true}
              onChange={onChange}
              dataConnected={data?.authDomainData || []}
              placement={data?.placement}
              value={formData.authDomain}
            />

            <TagInput
              name="messagingSenderId"
              placeholder="000000000000"
              labelName={'Messaging Sender ID'}
              handleId={'messagingSenderId'}
              isCollapsed={true}
              nodeId={id}
              isActive={isActiveEdge(edges, id, 'messagingSenderId', 'target')}
              isAdvanced={true}
              findColor={findColor}
              removeIcons
              className="nodrag"
              advancedCollapsed={true}
              onChange={onChange}
              dataConnected={data?.messagingSenderIdData || []}
              placement={data?.placement}
              value={formData.messagingSenderId}
            />

            <TagInput
              name="appId"
              placeholder="0:0000000:web:00000000"
              labelName={'App ID'}
              handleId={'appId'}
              isCollapsed={true}
              nodeId={id}
              isActive={isActiveEdge(edges, id, 'appId', 'target')}
              isAdvanced={true}
              findColor={findColor}
              removeIcons
              className="nodrag"
              advancedCollapsed={true}
              onChange={onChange}
              dataConnected={data?.appIdData || []}
              placement={data?.placement}
              value={formData.appId}
            />

            <TagInput
              name="projectId"
              placeholder="example-db-0000c"
              labelName={'Project ID'}
              handleId={'projectId'}
              isCollapsed={true}
              nodeId={id}
              isActive={isActiveEdge(edges, id, 'projectId', 'target')}
              isAdvanced={true}
              findColor={findColor}
              removeIcons
              className="nodrag"
              advancedCollapsed={true}
              onChange={onChange}
              dataConnected={data?.projectIdData || []}
              placement={data?.placement}
              value={formData.projectId}
            />

            <TagInput
              name="storageBucket"
              placeholder="example.firebasestorage.app"
              labelName={'Storage Bucket'}
              handleId={'storageBucket'}
              isCollapsed={true}
              nodeId={id}
              isActive={isActiveEdge(edges, id, 'storageBucket', 'target')}
              isAdvanced={true}
              findColor={findColor}
              removeIcons
              className="nodrag"
              advancedCollapsed={true}
              onChange={onChange}
              dataConnected={data?.storageBucketData || []}
              placement={data?.placement}
              value={formData.storageBucket}
            />

            <TagInput
              name="collection"
              placeholder="collectionName"
              labelName={'Collection'}
              handleId={'collection'}
              isCollapsed={true}
              nodeId={id}
              isActive={isActiveEdge(edges, id, 'collection', 'target')}
              isAdvanced={true}
              findColor={findColor}
              removeIcons
              className="nodrag"
              advancedCollapsed={true}
              onChange={onChange}
              dataConnected={data?.collectionData || []}
              placement={data?.placement}
              value={formData.collection}
            />

            <TagInput
              name="filter"
              placeholder={`{ "field": "fieldValue", "operator": "operatorValue", "value": "exampleValue" }`}
              labelName={'Filter'}
              handleId={'filter'}
              infoText="For more information, visit https://firebase.google.com/docs/firestore/query-data/queries"
              isCollapsed={true}
              nodeId={id}
              isActive={isActiveEdge(edges, id, 'filter', 'target')}
              isAdvanced={true}
              findColor={findColor}
              removeIcons
              className="nodrag"
              advancedCollapsed={true}
              onChange={onChange}
              dataConnected={data?.filterData || []}
              placement={data?.placement}
              value={formData.filter}
            />
          </>
        );
      default:
        return null;
    }
  };

  const handleRun = (run: string) => {
    nodeDataAutoSaveDynamic({
      newEdges: edges,
      setNodeState,
      setSaving,
      id,
      flowId,
      objectData: {
        run,
      },
      setSchema,
    });
  };

  const handleUploadFile = async (e: any) => {
    const file = e.target.files[0];

    if (!file) return;

    const minSize = 500;
    const maxSize = 16 * 1024;

    if (file.size < minSize) {
      setNotification({
        type: 'error',
        message: `The file is too small (${file.size} bytes). SSL certificates usually have at least ${minSize} bytes.`,
      });
      e.target.value = '';
      return;
    }

    if (file.size > maxSize) {
      setNotification({
        type: 'error',
        message: `The file is too large (${(file.size / 1024).toFixed(2)} KB). SSL certificates usually do not exceed ${maxSize / 1024} KB.`,
      });

      e.target.value = '';
      return;
    }

    const fileContent = await file.text();
    setFormData({
      ...formData,
      ca: fileContent,
    });

    setIsChangingDirectly(true);
    setWhoIsChanging({ name: 'ca', value: fileContent });
  };

  const handleInputFileChange = async (e: any) => {
    setFormData({
      ...formData,
      ca: e.target.value,
    });

    setIsChangingDirectly(true);
    setWhoIsChanging({ name: 'ca', value: e.target.value });
  };

  return (
    <Box className={isLoading ? 'node' : ''}>
      <Node
        id={id}
        edges={edges}
        type={type}
        handleRun={handleRun}
        data={data}
        isLoading={isLoading}
        btnText="Send Query"
        onSubmit={handleSendRequest}
        showTokensUsed={false}
      >
        <Box display={'flex'}>
          <Box p={'16px'} borderRight={'1px solid #EAECF0'}>
            <Box>
              <Label isAdvanced labelName="Select DBMS " />
              <Select
                value={formData.dbms}
                name="dbms"
                onChange={onChange}
                sx={{
                  bgcolor: '#EAECF0',
                  borderRadius: '8px',
                  border: 'none',
                  height: '30px',
                  textTransform: 'capitalize',
                  mb: '16px',
                  boxShadow: 'none',
                  '.MuiOutlinedInput-notchedOutline': { border: 0 },
                }}
                className="nodrag"
              >
                {Object.values(DatabaseOptions).map(option => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>

              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      name="ssl"
                      onChange={onChangeSwitch}
                      checked={formData.ssl}
                    />
                  }
                  label="SSL Connection"
                />

                {formData.ssl && <Box
                  display="flex"
                  justifyContent="space-between"
                  gap="4px"
                  alignItems="center"
                >
                  <TagInput
                    name={'ca'}
                    value={formData.ca}
                    findColor={findColor}
                    labelName={'SSL Certificate'}
                    isCollapsed={true}
                    nodeLabel={data?.label}
                    nodeId={id}
                    onChange={handleInputFileChange}
                    placeholder="Certificate goes here"
                    infoText="Enter certificate or select an upload"
                    hasSettings={false}
                    width="324px"
                    onIsOpenCollapsed={() => { }}
                  />

                  <Button
                    component="label"
                    role={undefined}
                    variant="contained"
                    tabIndex={-1}
                    startIcon={<UploadSimple size={18} style={{ color: '#FD746C' }} />}
                    sx={{
                      padding: '6px',
                      height: '100%',
                      backgroundColor: '#F9FAFB',
                      boxShadow: 'none',
                      border: '1px solid #FD746C',
                      minWidth: 0,
                      marginTop: '16px',
                      '& .MuiButton-startIcon': {
                        margin: 0,
                      },
                      '&:hover': {
                        backgroundColor: '#F9FAFB',
                        border: '1px solid #FD746C',
                        boxShadow: 'none',
                      },
                    }}
                  >
                    <VisuallyHiddenInput type="file" onChange={handleUploadFile} />
                  </Button>
                </Box>}

                {renderDatabaseInputs()}
              </Box>

              {/*  */}
            </Box>

            <Box mt={'16px'} mb={'16px'}>
              {hasError && (
                <FormHelperText
                  // error
                  sx={{
                    color: 'red',
                    fontSize: '12px',
                    fontWeight: 'bold',
                  }}
                >
                  An error occurred while executing the query.
                </FormHelperText>
              )}
            </Box>
          </Box>
          <Box p={'16px 24px 16px 16px'}>
            <OutputTextarea
              // previewResponses={previewResponses}
              previewIndex={data.previewIndex}
              value={data.text}
              activeHandle={isActiveEdge(edges, id, 'output', 'source')}
              placement={data?.placement}
              labelName={'Output'}
              nodeLabel={data?.label}
              nodeId={id}
              inputSize={data?.outputSize}
              collapsed={data?.collapsed || false}
              findColor={findColor}
            // onPreview={onPreview}
            // onChangePlacement={onChangePlacement}
            />
            {/* {formData.response && (
                <Box className='mt-5' data-response-section>
                  <Typography variant='h6'>Response status</Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '20px',
                    }}
                    className='d-flex my-2'
                  >
                  </Box>
                </Box>
              )} */}
          </Box>
        </Box>
      </Node>
    </Box>
  );
}

export default memo(SqlQuerier);
